import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, Send, Clock, Calendar, Mail, Phone, 
  Plus, Trash2, Edit, Users, Trophy, AlertTriangle, CheckCircle,
  Repeat, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MESSAGE_TEMPLATES = {
  game_day: {
    name: 'Game Day Reminder',
    icon: Trophy,
    defaultMessage: "🏀 GAME DAY! Don't forget - we play today at {time} vs {opponent} at {location}. Arrive 45 minutes early. Let's go Leopards!"
  },
  practice_reminder: {
    name: 'Practice Reminder',
    icon: Clock,
    defaultMessage: "Practice reminder: We have practice tomorrow at {time} at {location}. Please come prepared and on time!"
  },
  schedule_change: {
    name: 'Schedule Change',
    icon: AlertTriangle,
    defaultMessage: "⚠️ SCHEDULE CHANGE: {details}. Please update your calendars accordingly."
  },
  weekly_update: {
    name: 'Weekly Update',
    icon: Calendar,
    defaultMessage: "Weekly team update: This week we have {events}. Keep up the great work, team!"
  },
  custom: {
    name: 'Custom Message',
    icon: MessageSquare,
    defaultMessage: ''
  }
};

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function AutomatedMessagesPage({ user, onLogout }) {
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [automationSettings, setAutomationSettings] = useState({
    game_day_reminders: { enabled: false, hours_before: 24, channels: ['email'] },
    practice_reminders: { enabled: false, hours_before: 24, channels: ['email'] },
    schedule_changes: { enabled: true, channels: ['email', 'sms'] },
    weekly_updates: { enabled: false, day: 0, hour: 9, channels: ['email'] }
  });
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newMessage, setNewMessage] = useState({
    template: 'custom',
    message: '',
    recipients: 'all',
    channels: ['email'],
    schedule_type: 'once',
    schedule_date: '',
    schedule_time: '09:00',
    recurring_day: 0
  });

  const [smsStatus, setSmsStatus] = useState(null);

  useEffect(() => {
    loadScheduledMessages();
    loadAutomationSettings();
    loadSmsStatus();
  }, []);

  const loadSmsStatus = async () => {
    try {
      const response = await axios.get(`${API}/messaging/sms-status`);
      setSmsStatus(response.data);
    } catch (error) {
      console.error('Failed to load SMS status');
    }
  };

  const loadScheduledMessages = async () => {
    try {
      const response = await axios.get(`${API}/automated-messages?team_id=${user.team_id}`);
      setScheduledMessages(response.data);
    } catch (error) {
      console.error('Failed to load scheduled messages');
    }
  };

  const loadAutomationSettings = async () => {
    try {
      const response = await axios.get(`${API}/automated-messages/settings/${user.team_id}`);
      if (response.data) {
        setAutomationSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load automation settings');
    }
  };

  const saveAutomationSettings = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/automated-messages/settings`, {
        team_id: user.team_id,
        ...automationSettings
      });
      toast.success('Automation settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const scheduleMessage = async () => {
    if (!newMessage.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/automated-messages/schedule`, {
        team_id: user.team_id,
        ...newMessage,
        created_by: user.id
      });
      toast.success('Message scheduled successfully!');
      setShowScheduleDialog(false);
      setNewMessage({
        template: 'custom',
        message: '',
        recipients: 'all',
        channels: ['email'],
        schedule_type: 'once',
        schedule_date: '',
        schedule_time: '09:00',
        recurring_day: 0
      });
      loadScheduledMessages();
    } catch (error) {
      toast.error('Failed to schedule message');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteScheduledMessage = async (messageId) => {
    if (!confirm('Delete this scheduled message?')) return;
    try {
      await axios.delete(`${API}/automated-messages/${messageId}`);
      toast.success('Message deleted');
      loadScheduledMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const sendTestMessage = async (type) => {
    try {
      // Send as form data since backend expects Form(...)
      const formData = new FormData();
      formData.append('team_id', user.team_id);
      formData.append('type', type);
      const channels = automationSettings[type]?.channels || ['email'];
      channels.forEach(ch => formData.append('channels', ch));
      
      const response = await axios.post(`${API}/automated-messages/test`, formData);
      
      const results = response.data.results;
      let successMsg = 'Test message sent!';
      if (results) {
        const emailSent = results.email?.sent || 0;
        const smsSent = results.sms?.sent || 0;
        successMsg = `Test sent: ${emailSent} emails, ${smsSent} SMS`;
      }
      toast.success(successMsg);
    } catch (error) {
      console.error('Test message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    }
  };

  const sendScheduledNow = async (messageId) => {
    try {
      const response = await axios.post(`${API}/automated-messages/${messageId}/send-now`);
      const results = response.data.results;
      let successMsg = 'Message sent!';
      if (results) {
        const emailSent = results.email?.sent || 0;
        const smsSent = results.sms?.sent || 0;
        successMsg = `Sent: ${emailSent} emails, ${smsSent} SMS`;
      }
      toast.success(successMsg);
      loadScheduledMessages();
    } catch (error) {
      console.error('Send now error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const toggleChannel = (settingKey, channel) => {
    const currentChannels = automationSettings[settingKey]?.channels || [];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    
    setAutomationSettings({
      ...automationSettings,
      [settingKey]: {
        ...automationSettings[settingKey],
        channels: newChannels
      }
    });
  };

  const ChannelBadges = ({ channels, settingKey }) => (
    <div className="flex gap-2 mt-2">
      <button
        onClick={() => toggleChannel(settingKey, 'email')}
        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
          channels?.includes('email') 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
      >
        <Mail className="h-3 w-3 inline mr-1" />
        Email
      </button>
      <button
        onClick={() => toggleChannel(settingKey, 'sms')}
        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
          channels?.includes('sms') 
            ? 'bg-green-500 text-white' 
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
      >
        <Phone className="h-3 w-3 inline mr-1" />
        SMS
      </button>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="automated-messages-page">
        {/* Trial Account Warning */}
        {smsStatus?.is_trial_account && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-800">Twilio Trial Account Detected</h3>
              <p className="text-sm text-amber-700 mt-1">
                Your Twilio account is in trial mode. SMS can only be sent to <strong>verified phone numbers</strong>.
              </p>
              <a 
                href="https://console.twilio.com/us1/develop/phone-numbers/manage/verified" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-2 inline-block"
              >
                → Verify phone numbers in Twilio Console
              </a>
              <p className="text-xs text-amber-600 mt-2">
                To send to any number, upgrade your Twilio account by adding a payment method.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Automated Messages
            </h1>
            <p className="text-muted-foreground mt-2">Set up automatic notifications for your team via PAC</p>
          </div>
          <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
            <DialogTrigger asChild>
              <Button data-testid="schedule-message-btn">
                <Plus className="h-4 w-4 mr-2" />
                SCHEDULE MESSAGE
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">Schedule Message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Message Template</Label>
                  <Select
                    value={newMessage.template}
                    onValueChange={(value) => setNewMessage({
                      ...newMessage,
                      template: value,
                      message: MESSAGE_TEMPLATES[value]?.defaultMessage || ''
                    })}
                  >
                    <SelectTrigger data-testid="message-template">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(MESSAGE_TEMPLATES).map(([key, template]) => (
                        <SelectItem key={key} value={key}>{template.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Message</Label>
                  <Textarea
                    value={newMessage.message}
                    onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                    rows={4}
                    placeholder="Enter your message..."
                    data-testid="message-content"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {'{time}'}, {'{location}'}, {'{opponent}'}, {'{details}'} for dynamic content
                  </p>
                </div>

                <div>
                  <Label>Recipients</Label>
                  <Select
                    value={newMessage.recipients}
                    onValueChange={(value) => setNewMessage({ ...newMessage, recipients: value })}
                  >
                    <SelectTrigger data-testid="message-recipients">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone (Coaches & Parents)</SelectItem>
                      <SelectItem value="coaches">Coaches Only</SelectItem>
                      <SelectItem value="parents">Parents Only</SelectItem>
                      <SelectItem value="players">Players Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Delivery Channels</Label>
                  <div className="flex gap-2 mt-2">
                    {['email', 'sms'].map(channel => (
                      <button
                        key={channel}
                        onClick={() => {
                          const channels = newMessage.channels.includes(channel)
                            ? newMessage.channels.filter(c => c !== channel)
                            : [...newMessage.channels, channel];
                          setNewMessage({ ...newMessage, channels });
                        }}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          newMessage.channels.includes(channel)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {channel === 'email' && <Mail className="h-4 w-4 inline mr-1" />}
                        {channel === 'sms' && <Phone className="h-4 w-4 inline mr-1" />}
                        {channel.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Schedule Type</Label>
                  <Select
                    value={newMessage.schedule_type}
                    onValueChange={(value) => setNewMessage({ ...newMessage, schedule_type: value })}
                  >
                    <SelectTrigger data-testid="schedule-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">Send Once</SelectItem>
                      <SelectItem value="recurring">Recurring Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newMessage.schedule_type === 'once' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newMessage.schedule_date}
                        onChange={(e) => setNewMessage({ ...newMessage, schedule_date: e.target.value })}
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={newMessage.schedule_time}
                        onChange={(e) => setNewMessage({ ...newMessage, schedule_time: e.target.value })}
                        data-testid="schedule-time"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Day of Week</Label>
                      <Select
                        value={String(newMessage.recurring_day)}
                        onValueChange={(value) => setNewMessage({ ...newMessage, recurring_day: parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day, index) => (
                            <SelectItem key={index} value={String(index)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={newMessage.schedule_time}
                        onChange={(e) => setNewMessage({ ...newMessage, schedule_time: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <Button onClick={scheduleMessage} disabled={isLoading} className="w-full" data-testid="confirm-schedule-btn">
                  {isLoading ? 'SCHEDULING...' : 'SCHEDULE MESSAGE'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="automation" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="automation" data-testid="automation-tab">
              <Zap className="h-4 w-4 mr-2" />
              Automatic Triggers
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="scheduled-tab">
              <Clock className="h-4 w-4 mr-2" />
              Scheduled Messages ({scheduledMessages.length})
            </TabsTrigger>
          </TabsList>

          {/* Automatic Triggers Tab */}
          <TabsContent value="automation" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Game Day Reminders */}
              <Card className="border-2">
                <CardHeader className="border-b bg-orange-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-orange-600" />
                      Game Day Reminders
                    </CardTitle>
                    <Switch
                      checked={automationSettings.game_day_reminders?.enabled}
                      onCheckedChange={(checked) => setAutomationSettings({
                        ...automationSettings,
                        game_day_reminders: { ...automationSettings.game_day_reminders, enabled: checked }
                      })}
                      data-testid="game-day-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Automatically send reminders before game days with time, location, and opponent info.
                  </p>
                  <div>
                    <Label>Hours Before Game</Label>
                    <Select
                      value={String(automationSettings.game_day_reminders?.hours_before || 24)}
                      onValueChange={(value) => setAutomationSettings({
                        ...automationSettings,
                        game_day_reminders: { ...automationSettings.game_day_reminders, hours_before: parseInt(value) }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">12 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Delivery Channels</Label>
                    <ChannelBadges 
                      channels={automationSettings.game_day_reminders?.channels} 
                      settingKey="game_day_reminders" 
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => sendTestMessage('game_day_reminders')}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </CardContent>
              </Card>

              {/* Practice Reminders */}
              <Card className="border-2">
                <CardHeader className="border-b bg-blue-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      Practice Reminders
                    </CardTitle>
                    <Switch
                      checked={automationSettings.practice_reminders?.enabled}
                      onCheckedChange={(checked) => setAutomationSettings({
                        ...automationSettings,
                        practice_reminders: { ...automationSettings.practice_reminders, enabled: checked }
                      })}
                      data-testid="practice-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Send automatic reminders before practice sessions.
                  </p>
                  <div>
                    <Label>Hours Before Practice</Label>
                    <Select
                      value={String(automationSettings.practice_reminders?.hours_before || 24)}
                      onValueChange={(value) => setAutomationSettings({
                        ...automationSettings,
                        practice_reminders: { ...automationSettings.practice_reminders, hours_before: parseInt(value) }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">12 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Delivery Channels</Label>
                    <ChannelBadges 
                      channels={automationSettings.practice_reminders?.channels} 
                      settingKey="practice_reminders" 
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => sendTestMessage('practice_reminders')}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </CardContent>
              </Card>

              {/* Schedule Changes */}
              <Card className="border-2">
                <CardHeader className="border-b bg-red-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      Schedule Change Alerts
                    </CardTitle>
                    <Switch
                      checked={automationSettings.schedule_changes?.enabled}
                      onCheckedChange={(checked) => setAutomationSettings({
                        ...automationSettings,
                        schedule_changes: { ...automationSettings.schedule_changes, enabled: checked }
                      })}
                      data-testid="schedule-change-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Automatically notify everyone when schedule changes are made.
                  </p>
                  <div>
                    <Label>Delivery Channels</Label>
                    <ChannelBadges 
                      channels={automationSettings.schedule_changes?.channels} 
                      settingKey="schedule_changes" 
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => sendTestMessage('schedule_changes')}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </CardContent>
              </Card>

              {/* Weekly Updates */}
              <Card className="border-2">
                <CardHeader className="border-b bg-green-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-green-600" />
                      Weekly Team Updates
                    </CardTitle>
                    <Switch
                      checked={automationSettings.weekly_updates?.enabled}
                      onCheckedChange={(checked) => setAutomationSettings({
                        ...automationSettings,
                        weekly_updates: { ...automationSettings.weekly_updates, enabled: checked }
                      })}
                      data-testid="weekly-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Send weekly summary with upcoming schedule and team updates.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Day</Label>
                      <Select
                        value={String(automationSettings.weekly_updates?.day || 0)}
                        onValueChange={(value) => setAutomationSettings({
                          ...automationSettings,
                          weekly_updates: { ...automationSettings.weekly_updates, day: parseInt(value) }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day, index) => (
                            <SelectItem key={index} value={String(index)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Select
                        value={String(automationSettings.weekly_updates?.hour || 9)}
                        onValueChange={(value) => setAutomationSettings({
                          ...automationSettings,
                          weekly_updates: { ...automationSettings.weekly_updates, hour: parseInt(value) }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(24)].map((_, i) => (
                            <SelectItem key={i} value={String(i)}>{i.toString().padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Delivery Channels</Label>
                    <ChannelBadges 
                      channels={automationSettings.weekly_updates?.channels} 
                      settingKey="weekly_updates" 
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => sendTestMessage('weekly_updates')}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveAutomationSettings} disabled={isSaving} data-testid="save-automation-btn">
                {isSaving ? 'SAVING...' : 'SAVE ALL SETTINGS'}
              </Button>
            </div>
          </TabsContent>

          {/* Scheduled Messages Tab */}
          <TabsContent value="scheduled" className="space-y-6">
            {scheduledMessages.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mt-4">No scheduled messages yet</p>
                  <Button className="mt-4" onClick={() => setShowScheduleDialog(true)}>
                    SCHEDULE YOUR FIRST MESSAGE
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {scheduledMessages.map((message) => (
                  <Card key={message.id} className="border-2" data-testid={`scheduled-message-${message.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {message.schedule_type === 'recurring' ? (
                              <Badge variant="outline" className="bg-purple-100">
                                <Repeat className="h-3 w-3 mr-1" />
                                Recurring
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-blue-100">
                                <Clock className="h-3 w-3 mr-1" />
                                One-time
                              </Badge>
                            )}
                            <Badge variant="outline">
                              <Users className="h-3 w-3 mr-1" />
                              {message.recipients}
                            </Badge>
                            {message.channels?.map(channel => (
                              <Badge key={channel} variant="secondary" className="text-xs">
                                {channel}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-sm">{message.message}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {message.schedule_type === 'recurring' 
                              ? `Every ${DAYS_OF_WEEK[message.recurring_day]} at ${message.schedule_time}`
                              : `Scheduled for ${new Date(message.schedule_date + 'T' + message.schedule_time).toLocaleString()}`
                            }
                          </p>
                          {message.status === 'sent' && (
                            <Badge className="mt-2 bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Sent
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {message.status !== 'sent' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendScheduledNow(message.id)}
                              data-testid={`send-now-${message.id}`}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Send Now
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => deleteScheduledMessage(message.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
